import React, { FunctionComponent, useState } from "react";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { InputAdornment, TextField } from "@material-ui/core";
import useStyles from "../styles";
import { NotificationTypeSelect } from "./NotificationTypeSelect";
import { CreateEmailTemplate } from "../../../models/Marketing";
import { useI18n } from "../../../hooks/useI18n";

interface CreateEmailTemplateDialogProps {
  visible: boolean;
  disabled: boolean;
  onCancel: () => void;
  onConfirm: (model: CreateEmailTemplate) => void;
}

interface State {
  description?: string;
  notificationType?: string;
  sendgridIdSwedish?: string;
  sendgridIdEnglish?: string;
  unsubscribeId?: number;
  senderEmail?: string;
  senderName?: string;
}

export const CreateEmailTemplateDialog: FunctionComponent<
  CreateEmailTemplateDialogProps
> = props => {
  const classes = useStyles();
  const { t } = useI18n();
  const [state, setState] = useState<State>({
    unsubscribeId: 12802,
    senderEmail: "dialog@lifvs.com",
    senderName: t("common.messages.teamLifvs")
  });

  const isValid = (): boolean => {
    if (!state.description) return false;

    if (!state.notificationType) return false;

    if (!state.sendgridIdSwedish) return false;

    if (!state.unsubscribeId) return false;

    if (!state.senderEmail) return false;

    if (!state.senderName) return false;

    return true;
  };

  const handleConfirm = (): boolean => {
    if (!isValid()) return false;

    props.onConfirm({
      description: state.description ?? "",
      notificationType: state.notificationType ?? "",
      englishSendGridTemplateId: state.sendgridIdEnglish,
      swedishSendGridTemplateId: state.sendgridIdSwedish ?? "",
      unsubscribeId: state.unsubscribeId?.toString() ?? "",
      senderEmail: state.senderEmail ?? "",
      senderName: state.senderName ?? ""
    });

    return false;
  };

  return (
    <ConfirmDialog
      {...props}
      onConfirm={handleConfirm}
      titleText={t("marketing.emailTemplates.createDialog.title")}
      confirmText={t("common.buttons.save")}
      confirmDisabled={!isValid() || props.disabled}
      cancelDisabled={props.disabled}
      disableBackdropClick={props.disabled}
    >
      <div className={classes.minimum500}>
        <TextField
          required
          disabled={props.disabled}
          className={classes.field}
          label={t("common.formFields.description")}
          type="text"
          value={state.description ?? ""}
          onChange={e =>
            setState({ ...state, description: e.target.value as string })
          }
        />
        <NotificationTypeSelect
          required
          disabled={props.disabled}
          className={classes.field}
          value={state.notificationType}
          onSelected={notificationType =>
            setState({ ...state, notificationType })
          }
        />
        <TextField
          required
          disabled={props.disabled}
          className={classes.field}
          label={t("marketing.emailTemplates.createDialog.sendGridIdSwedish")}
          type="text"
          value={state.sendgridIdSwedish ?? ""}
          onChange={e =>
            setState({ ...state, sendgridIdSwedish: e.target.value as string })
          }
        />
        <TextField
          disabled={props.disabled}
          className={classes.field}
          label={t("marketing.emailTemplates.createDialog.sendGridIdEnglish")}
          type="text"
          value={state.sendgridIdEnglish ?? ""}
          onChange={e =>
            setState({ ...state, sendgridIdEnglish: e.target.value as string })
          }
        />
        <TextField
          required
          disabled={props.disabled}
          className={classes.field}
          label={t("common.formFields.unsubscribeId")}
          type="number"
          value={state.unsubscribeId ?? ""}
          onChange={e =>
            setState({ ...state, unsubscribeId: Number(e.target.value) })
          }
        />
        <TextField
          required
          disabled={props.disabled}
          className={classes.field}
          label={t("common.formFields.senderEmail")}
          type="text"
          value={state.senderEmail ?? ""}
          onChange={e =>
            setState({ ...state, senderEmail: e.target.value as string })
          }
        />
        <TextField
          required
          disabled={props.disabled}
          className={classes.field}
          label={t("common.formFields.senderName")}
          type="text"
          value={state.senderName ?? ""}
          onChange={e =>
            setState({ ...state, senderName: e.target.value as string })
          }
        />
      </div>
    </ConfirmDialog>
  );
};
